import { api } from '../api.ts';
import { compact, html, mount, raw } from '../html.ts';
import { formatDate, formatMoney, GATE_STATUS, isOverdue, options, PHASE_STATUS, t } from '../i18n.ts';
import { canWrite } from '../state.ts';
import { confirmDialog, emptyState, field, openFormDialog, optionList, select, statusBadge, textarea, toast, toastError } from '../ui.ts';

/**
 * Phases and gates. The phase rail and gate checklist are an app-local
 * prototype of a Purple UI "gate stepper": an ordered list of phases, each
 * with its gates, measurable criteria and a decision that is logged.
 */

interface Criterion {
  id: string;
  label: string;
  target: string | null;
  actual: string | null;
  met: boolean | null;
}
interface Gate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  phase_id: string | null;
  due_on: string | null;
  status: string;
  decision_id: string | null;
  decided_at: string | null;
  criteria: Criterion[];
}
interface Phase {
  id: string;
  name: string;
  description: string | null;
  position: number;
  status: string;
  starts_on: string | null;
  ends_on: string | null;
  budget_planned: number | null;
  budget_actual: number | null;
}

function criterionRow(c: Criterion, editable: boolean) {
  const state = c.met === true ? 'met' : c.met === false ? 'unmet' : 'open';
  return html`<li class="pma-criterion pma-criterion--${state}" data-criterion="${c.id}">
    <span class="pma-criterion__mark" aria-hidden="true">${c.met === true ? '✓' : c.met === false ? '✗' : '○'}</span>
    <div class="pma-criterion__text"><span>${c.label}</span>${c.target ? html`<span class="pma-muted pma-small">${t('Ziel')} ${c.target}</span>` : ''}</div>
    ${
      editable
        ? html`<input class="p-input pma-criterion__actual" data-p-size="sm" data-actual value="${c.actual ?? ''}" placeholder="${t('Ist-Wert')}" aria-label="${t('Ist-Wert für {label}', { label: c.label })}">
          <select class="p-select pma-criterion__state" data-p-size="sm" data-met aria-label="${t('Bewertung für {label}', { label: c.label })}">
            ${raw(options({ '': t('Offen'), true: t('Erfüllt'), false: t('Nicht erfüllt') }, c.met === null ? '' : String(c.met)))}
          </select>`
        : html`<span class="pma-small">${c.actual ?? ''}</span><span class="pma-small">${c.met === true ? t('Erfüllt') : c.met === false ? t('Nicht erfüllt') : t('Offen')}</span>`
    }
  </li>`;
}

function gateCard(g: Gate) {
  const editable = canWrite() && g.status === 'pending';
  const met = g.criteria.filter((c) => c.met === true).length;
  return html`<article class="p-card pma-gate pma-gate--${g.status}" data-gate="${g.id}">
    <div class="p-card__header pma-gate__header">
      <div><span class="pma-gate__code">${g.code}</span> <strong>${g.name}</strong>
        <div class="pma-muted pma-small">${g.criteria.length ? t('{met} von {total} Kriterien erfüllt', { met, total: g.criteria.length }) : t('Keine Kriterien')} ·
          ${t('fällig')} <span class="${g.status === 'pending' && isOverdue(g.due_on) ? 'pma-overdue' : ''}">${formatDate(g.due_on)}</span></div></div>
      ${statusBadge(g.status, GATE_STATUS)}
    </div>
    <div class="p-card__body">
      ${g.description ? html`<p class="pma-small">${g.description}</p>` : ''}
      <progress class="pma-gate__progress" max="${Math.max(1, g.criteria.length)}" value="${met}" aria-label="${t('Erfüllte Kriterien')}"></progress>
      <ul class="pma-criteria">${g.criteria.map((c) => criterionRow(c, editable))}</ul>
      ${editable ? html`<form class="pma-inline-add" data-add-criterion>
        <input class="p-input" data-p-size="sm" name="label" placeholder="${t('Kriterium hinzufügen')}" aria-label="${t('Neues Kriterium')}" required>
        <input class="p-input" data-p-size="sm" name="target" placeholder="${t('Ziel (optional)')}" aria-label="${t('Zielwert')}">
        <button class="p-button p-button--sm" type="submit">${t('Hinzufügen')}</button></form>` : ''}
    </div>
    ${
      canWrite()
        ? html`<div class="p-card__footer pma-gate__actions">${
            g.status === 'pending'
              ? html`<button class="p-button p-button--primary p-button--sm" type="button" data-decide="passed">${t('Bestanden')}</button>
                <button class="p-button p-button--sm" type="button" data-decide="waived">${t('Mit Ausnahme freigeben')}</button>
                <button class="p-button p-button--ghost p-button--sm" type="button" data-decide="failed">${t('Nicht bestanden')}</button>
                <button class="p-button p-button--ghost p-button--sm pma-push" type="button" data-edit-gate>${t('Bearbeiten')}</button>`
              : html`<span class="pma-muted pma-small">${t('Entschieden am {date} · im Entscheidungsprotokoll dokumentiert', { date: formatDate(g.decided_at) })}</span>`
          }</div>`
        : ''
    }
  </article>`;
}

export async function renderGates(target: HTMLElement, projectId: string, currency: string, onChange: () => void) {
  let phases: Phase[] = [];
  let gates: Gate[] = [];

  async function load() {
    const [p, g] = await Promise.all([
      api.get<{ items: Phase[] }>(`/projects/${projectId}/phases`),
      api.get<{ items: Gate[] }>(`/projects/${projectId}/gates`),
    ]);
    phases = p.items;
    gates = g.items;
    draw();
  }

  function draw() {
    const unassigned = gates.filter((g) => !g.phase_id || !phases.some((p) => p.id === g.phase_id));
    mount(
      target,
      html`<div class="p-table-toolbar pma-toolbar">
        <span class="p-table-toolbar__status pma-muted">${t('{phases} Phasen · {gates} offene Gates', { phases: phases.length, gates: gates.filter((g) => g.status === 'pending').length })}</span>
        <div class="p-table-toolbar__actions">${
          canWrite()
            ? html`<button class="p-button" type="button" data-new-phase>${t('Neue Phase')}</button>
              <button class="p-button p-button--primary" type="button" data-new-gate>${t('Neues Gate')}</button>`
            : ''
        }</div>
      </div>
      ${
        !phases.length && !gates.length
          ? emptyState(t('Keine Phasen oder Gates'), t('Strukturiere das Projekt in Phasen und lege Freigabe-Gates mit messbaren Kriterien fest.'))
          : html`<ol class="pma-phase-rail" aria-label="${t('Phasenverlauf')}">${phases.map(
              (p) => html`<li class="pma-phase-rail__step pma-phase-rail__step--${p.status}" aria-current="${p.status === 'active' ? 'step' : 'false'}">
                <span class="pma-phase-rail__dot" aria-hidden="true"></span><span class="pma-small">${p.name}</span></li>`,
            )}</ol>
            <div class="pma-phases">${phases.map((p) => {
              const phaseGates = gates.filter((g) => g.phase_id === p.id);
              return html`<section class="pma-phase" aria-labelledby="phase-${p.id}">
                <header class="pma-phase__header">
                  <div><h3 id="phase-${p.id}">${p.name}</h3>
                    <p class="pma-muted pma-small">${formatDate(p.starts_on)} – ${formatDate(p.ends_on)}
                      ${p.budget_planned !== null ? html` · ${t('Budget')} ${formatMoney(p.budget_planned, currency)}${p.budget_actual !== null ? html` / ${t('Ist')} ${formatMoney(p.budget_actual, currency)}` : ''}` : ''}</p></div>
                  <div class="pma-list__meta">${statusBadge(p.status, PHASE_STATUS)}
                    ${canWrite() ? html`<button class="p-button p-button--ghost p-button--sm" type="button" data-edit-phase="${p.id}">${t('Bearbeiten')}</button>` : ''}</div>
                </header>
                ${phaseGates.length ? html`<div class="pma-gates">${phaseGates.map(gateCard)}</div>` : ''}
              </section>`;
            })}
            ${unassigned.length ? html`<section class="pma-phase"><header class="pma-phase__header"><h3>${t('Gates ohne Phase')}</h3></header><div class="pma-gates">${unassigned.map(gateCard)}</div></section>` : ''}
            </div>`
      }`,
    );
  }

  async function editPhase(phase: Phase | null) {
    const dialog = openFormDialog({
      title: phase ? t('Phase bearbeiten') : t('Neue Phase'),
      fields: html`${field(t('Name'), 'name', 'text', { required: true, value: phase?.name, span: true })}
        ${select(t('Status'), 'status', options(PHASE_STATUS, phase?.status ?? 'planned'))}
        ${field(t('Reihenfolge'), 'position', 'number', { value: phase?.position ?? phases.length, attrs: 'min="0"' })}
        ${field(t('Start'), 'starts_on', 'date', { value: phase?.starts_on })}
        ${field(t('Ende'), 'ends_on', 'date', { value: phase?.ends_on })}
        ${field(t('Budget geplant'), 'budget_planned', 'number', { value: phase?.budget_planned, attrs: 'min="0" step="100"' })}
        ${field(t('Budget Ist'), 'budget_actual', 'number', { value: phase?.budget_actual, attrs: 'min="0" step="100"' })}
        ${textarea(t('Beschreibung'), 'description', { value: phase?.description, rows: 2 })}`,
      onSubmit: async (data) => {
        if (phase) await api.patch(`/phases/${phase.id}`, data);
        else await api.post(`/projects/${projectId}/phases`, compact(data));
        await load();
        onChange();
      },
    });
    if (phase) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'p-button p-button--ghost pma-danger-text';
      remove.textContent = t('Löschen');
      remove.addEventListener('click', async () => {
        dialog.close();
        if (await confirmDialog(t('Phase löschen?'), t('„{name}“ wird gelöscht. Aufgaben und Gates bleiben erhalten, verlieren aber die Zuordnung.', { name: phase.name }))) {
          await api.delete(`/phases/${phase.id}`).catch(toastError);
          await load();
        }
      });
      dialog.querySelector('.p-dialog__actions')!.prepend(remove);
    }
  }

  async function editGate(gate: Gate | null) {
    const phaseOptions = optionList(phases.map((p) => ({ id: p.id, label: p.name })), gate?.phase_id, t('Keine Phase'));
    const dialog = openFormDialog({
      title: gate ? t('Gate {code} bearbeiten', { code: gate.code }) : t('Neues Gate'),
      fields: html`${field(t('Code'), 'code', 'text', { required: true, value: gate?.code, hint: t('z. B. G1'), attrs: 'maxlength="20"' })}
        ${field(t('Name'), 'name', 'text', { required: true, value: gate?.name })}
        ${select(t('Phase'), 'phase_id', phaseOptions)}
        ${field(t('Fällig'), 'due_on', 'date', { value: gate?.due_on })}
        ${textarea(t('Beschreibung'), 'description', { value: gate?.description, rows: 2 })}
        ${gate ? '' : textarea(t('Kriterien'), 'criteria_text', { rows: 4, hint: t('Ein Kriterium pro Zeile; Zielwert optional nach „|“, z. B. „Erfolgsquote | ≥ 90 %“') })}`,
      onSubmit: async (data) => {
        if (gate) {
          await api.patch(`/gates/${gate.id}`, data);
        } else {
          const criteria = String(data.criteria_text ?? '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
              const [label, target] = line.split('|').map((s) => s.trim());
              return target ? { label: label!, target } : { label: label! };
            });
          delete data.criteria_text;
          await api.post(`/projects/${projectId}/gates`, { ...compact(data), criteria });
        }
        await load();
        onChange();
      },
    });
    if (gate) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'p-button p-button--ghost pma-danger-text';
      remove.textContent = t('Löschen');
      remove.addEventListener('click', async () => {
        dialog.close();
        if (await confirmDialog(t('Gate löschen?'), t('Gate {code} und seine Kriterien werden gelöscht.', { code: gate.code }))) {
          await api.delete(`/gates/${gate.id}`).catch(toastError);
          await load();
        }
      });
      dialog.querySelector('.p-dialog__actions')!.prepend(remove);
    }
  }

  function decide(gate: Gate, outcome: string) {
    const labels: Record<string, string> = { passed: t('bestanden'), waived: t('mit Ausnahme freigeben'), failed: t('nicht bestanden') };
    openFormDialog({
      title: `Gate ${gate.code}: ${labels[outcome]}`,
      description: t('Die Entscheidung wird mit dem Kriterienstand im Entscheidungsprotokoll festgehalten.'),
      fields: textarea(outcome === 'waived' ? t('Begründung der Ausnahme (Pflicht)') : t('Begründung'), 'rationale', { rows: 4, required: outcome === 'waived' }),
      submitLabel: t('Entscheidung festhalten'),
      danger: outcome === 'failed',
      onSubmit: async (data) => {
        await api.post(`/gates/${gate.id}/decision`, compact({ outcome, rationale: data.rationale }));
        toast(t('Gate {code} entschieden', { code: gate.code }), t('Im Entscheidungsprotokoll dokumentiert.'));
        await load();
        onChange();
      },
    });
  }

  async function saveCriterion(id: string, patch: Record<string, unknown>) {
    try {
      const updated = await api.patch<Criterion>(`/gate-criteria/${id}`, patch);
      for (const gate of gates) {
        const index = gate.criteria.findIndex((c) => c.id === id);
        if (index >= 0) gate.criteria[index] = updated;
      }
      draw();
      target.querySelector<HTMLElement>(`[data-criterion="${id}"] [data-met]`)?.focus();
    } catch (error) {
      toastError(error);
    }
  }

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    const gate = gates.find((g) => g.id === element.closest<HTMLElement>('[data-gate]')?.dataset.gate);
    if (element.closest('[data-new-phase]')) void editPhase(null);
    else if (element.closest('[data-new-gate]')) void editGate(null);
    else if (element.closest('[data-edit-phase]')) {
      const phase = phases.find((p) => p.id === element.closest<HTMLElement>('[data-edit-phase]')!.dataset.editPhase);
      if (phase) void editPhase(phase);
    } else if (gate && element.closest('[data-edit-gate]')) void editGate(gate);
    else if (gate && element.closest('[data-decide]')) decide(gate, element.closest<HTMLElement>('[data-decide]')!.dataset.decide!);
  });

  target.addEventListener('change', (event) => {
    const element = event.target as HTMLInputElement;
    const id = element.closest<HTMLElement>('[data-criterion]')?.dataset.criterion;
    if (!id) return;
    if (element.matches('[data-met]')) void saveCriterion(id, { met: element.value === '' ? null : element.value === 'true' });
    if (element.matches('[data-actual]')) void saveCriterion(id, { actual: element.value.trim() || null });
  });

  target.addEventListener('submit', async (event) => {
    const form = (event.target as HTMLElement).closest<HTMLFormElement>('[data-add-criterion]');
    if (!form) return;
    event.preventDefault();
    const gateId = form.closest<HTMLElement>('[data-gate]')!.dataset.gate!;
    const label = (form.elements.namedItem('label') as HTMLInputElement).value.trim();
    const targetValue = (form.elements.namedItem('target') as HTMLInputElement).value.trim();
    if (!label) return;
    try {
      await api.post(`/gates/${gateId}/criteria`, compact({ label, target: targetValue || null }));
      await load();
      target.querySelector<HTMLInputElement>(`[data-gate="${gateId}"] [data-add-criterion] input`)?.focus();
    } catch (error) {
      toastError(error);
    }
  });

  await load();
}
